import type React from 'react'
import { defineMessages, useIntl } from 'react-intl'
import styled, { css } from 'styled-components'

import { ButtonBase } from '@components/ui/Buttons'

import { XmarkRegularIcon } from '../svgIcons'

const messages = defineMessages({
  delete: {
    id: 'components.ui.Chip.delete',
    defaultMessage: 'Törlés',
  },
})

const ClearButton = styled(ButtonBase)`
  border: none;
  background: none;
  color: inherit;
  padding: 0;
  align-items: center;
  justify-content: center;

  width: 14px;
  height: 14px;

  &:hover,
  &:focus {
    color: ${({ theme }) => theme.colors.common.black};
  }

  &:before {
    display: none;
  }
`

export const CHIP_STYLE_VARIABLES = {
  backgroundColor: '--chip-background-color',
  color: '--chip-color',
  fontSize: '--chip-font-size',
  fontWeight: '--chip-font-weight',
  height: '--chip-height',
  lineHeight: '--chip-line-height',
  padding: '--chip-padding',
}

const StyledContainer = styled.div<{
  $variant: 'filled' | 'outlined'
}>`
  --background-color: var(${CHIP_STYLE_VARIABLES.backgroundColor}, var(--inner-chip-background-color, transparent));
  --color: var(${CHIP_STYLE_VARIABLES.color}, var(--inner-chip-color, #000));
  --font-size: var(${CHIP_STYLE_VARIABLES.fontSize}, var(--inner-chip-font-size, 16px));
  --font-weight: var(${CHIP_STYLE_VARIABLES.fontWeight}, var(--inner-chip-font-weight, 500));
  --height: var(${CHIP_STYLE_VARIABLES.height}, var(--inner-chip-height, 28px));
  --line-height: var(${CHIP_STYLE_VARIABLES.lineHeight}, var(--inner-chip-line-height, 1.43));
  --padding: var(${CHIP_STYLE_VARIABLES.padding}, var(--inner-chip-padding, 12px));

  color: var(--color);
  height: var(--height);
  padding: 0 var(--padding);
  font-size: var(--font-size);
  font-weight: var(--font-weight);
  line-height: var(--line-height);
  margin: 0;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  cursor: default;
  gap: 5px;
  border-radius: 4px;

  ${({ $variant }) => css`
    background-color: ${$variant === 'filled' ? 'var(--background-color, transparent)' : 'transparent'};
    border: ${$variant === 'outlined' ? `1px solid var(--color)` : 'none'};
  `}
`

export interface ChipProps extends React.HTMLAttributes<HTMLDivElement> {
  backgroundColor?: string
  bold?: boolean
  className?: string
  color: string
  label: React.ReactNode
  onDelete?: () => void
  size?: 'default' | 'small'
  tag?: 'span' | 'p'
  variant?: 'filled' | 'outlined'
}

export function Chip({
  backgroundColor,
  bold = false,
  color,
  label,
  onDelete,
  size = 'default',
  tag = 'p',
  variant = 'filled',
  ...rest
}: ChipProps) {
  const { formatMessage } = useIntl()

  return (
    <StyledContainer
      {...rest}
      as={tag}
      style={{
        ...rest.style,
        '--inner-chip-background-color': backgroundColor || color,
        '--inner-chip-color': color,
        '--inner-chip-font-size': size === 'default' ? '16px' : '12px',
        '--inner-chip-font-weight': bold ? '700' : '500',
        '--inner-chip-height': size === 'default' ? '28px' : '20px',
        '--inner-chip-line-height': size === 'default' ? '1.43' : '16px',
        '--inner-chip-padding': size === 'default' ? '12px' : '5px',
      }}
      $variant={variant}
    >
      {label}
      {onDelete && (
        <ClearButton type="button" onClick={onDelete} aria-label={formatMessage(messages.delete)}>
          <XmarkRegularIcon size={8} />
        </ClearButton>
      )}
    </StyledContainer>
  )
}
